import { useEffect, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation } from 'react-router-dom';

import { Button } from '../ui/button';
import { Notice } from '../ui/notice';
import { Spinner } from '../ui/spinner';
import { isExpiredSessionError, returnToFromState } from './oidc';
import { restartLogin } from './restart-login';

/**
 * Auth gate (admin-shell-oidc-rbac B1/B2): an unauthenticated visitor is sent
 * through the OIDC Authorization-Code + PKCE login. Children render only once a
 * session exists. The pre-login location rides the OIDC `state` round-trip and
 * is restored off the `/callback` landing (see `returnToFromState`), so a login
 * never strands the user on "Not found".
 *
 * An EXPIRED session is not an error state. When silent renew fails because the
 * Keycloak session is gone, Keycloak's `error_description` ("Token is not
 * active") used to land here as a terminal red banner: the auto-login effect is
 * guarded on `!auth.error` so it never retried, the banner replaces the whole
 * shell so `TopBar`'s Log out wasn't reachable, and the dead user stayed in
 * localStorage so reloading re-armed the same failure. Now `restartLogin` tears
 * the dead session down and bounces to the login (see `isExpiredSessionError`
 * for why only renew failures qualify).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const returnTo =
    location.pathname === '/callback' ? undefined : `${location.pathname}${location.search}`;
  const sessionExpired = isExpiredSessionError(auth.error);

  useEffect(() => {
    if (sessionExpired) {
      // `restartLogin`'s own guard absorbs the repeat firing caused by `auth`
      // taking a new identity on every provider dispatch, so no ref is needed.
      void restartLogin(auth, returnTo);
      return;
    }
    if (!auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator && !auth.error) {
      void auth.signinRedirect(returnTo === undefined ? undefined : { state: { returnTo } });
    }
  }, [auth, returnTo, sessionExpired]);

  if (auth.error && !sessionExpired) {
    return (
      <Notice variant="error" role="alert" className="m-6 max-w-[600px]">
        <p>Authentication error: {auth.error.message}</p>
        {/* Escape hatch: this banner replaces the whole shell, so without it a
            non-recoverable auth error leaves no way out but clearing storage. */}
        <Button size="sm" className="mt-2" onClick={() => void restartLogin(auth, returnTo)}>
          Sign in again
        </Button>
      </Notice>
    );
  }
  if (sessionExpired || auth.isLoading || auth.activeNavigator || !auth.isAuthenticated) {
    return (
      <Spinner
        label={sessionExpired ? 'Session expired — signing you in again…' : 'Signing in…'}
        className="mt-16"
      />
    );
  }
  if (location.pathname === '/callback') {
    return <Navigate to={returnToFromState(auth.user?.state)} replace />;
  }
  return <>{children}</>;
}
